import React from 'react';
import { ParsedBusinessResult } from 'services/businessBilling/parseResponse';
import { MAX_LOOKUP_RETRIES, isLookupRetryable, retryDelayMs } from 'services/businessBilling/retryPolicy';
import { useCooldown } from './useCooldown';

export interface LookupRetryOptions {
    lookup: { phase: 'idle' | 'waiting' | 'invalid' | 'loading' | 'done'; cui?: string; result?: ParsedBusinessResult };
    scopeKey: string;
    verificationRejected?: boolean; // The submit refused the verification now shown: a new lookup is the way forward.
    onRetry: () => void; // The lookup hook's own retry.
}

export interface LookupRetry {
    canRetry: boolean;
    retryAvailableAt: number | null; // Epoch ms from which a retry is allowed; the UI can render a countdown from it.
    retriesLeft: number;
    retry: () => void;
}

/**
 * Decides when a manual lookup retry is allowed: only for a retryable answer, after the wait the server asked for
 * (at least 1 s), and at most MAX_LOOKUP_RETRIES times per CUI. It never retries by itself.
 */
export const useLookupRetry = ({ lookup, scopeKey, verificationRejected = false, onRetry }: LookupRetryOptions): LookupRetry => {
    const result = lookup.phase === 'done' ? lookup.result : undefined;

    // The moment an answer arrives is stamped once per answer, in the same render, so the wait is never missing.
    const stamp = React.useRef<{ result: ParsedBusinessResult; at: number } | null>(null);
    if (result === undefined) {
        stamp.current = null;
    }
    else if (stamp.current === null || stamp.current.result !== result) {
        stamp.current = { result, at: Date.now() };
    }

    const retryable = result !== undefined && isLookupRetryable(result, { verificationRejected });
    const retryAvailableAt = retryable && stamp.current ? stamp.current.at + retryDelayMs(stamp.current.result) : null;
    const cooling = useCooldown(retryAvailableAt);

    const key = lookup.cui !== undefined ? `${scopeKey}|${lookup.cui}` : null;
    const [counter, setCounter] = React.useState<{ key: string; count: number } | null>(null);
    const used = counter !== null && counter.key === key ? counter.count : 0;
    const retriesLeft = Math.max(0, MAX_LOOKUP_RETRIES - used);

    const canRetry = retryable && !cooling && retriesLeft > 0;

    const latest = React.useRef({ canRetry, key, onRetry });
    latest.current = { canRetry, key, onRetry };

    const retry = React.useCallback(() => {
        const current = latest.current;
        if (!current.canRetry || current.key === null) {
            return;
        }
        const { key: retryKey } = current;
        setCounter((previous) => ({
            key: retryKey,
            count: (previous !== null && previous.key === retryKey ? previous.count : 0) + 1,
        }));

        current.onRetry();

    }, []);

    return { canRetry, retryAvailableAt, retriesLeft, retry };
};
